/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Inject, Input, OnChanges, inject} from '@angular/core';
import {UIRouterModule} from '@uirouter/angular';

import template from './blocklist-edit-usage.component.html';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {AdUnitApiService, AdUnitModel} from '../ad-unit/ad-unit-api.service';
import {SignalAdminResourceApi, SignalAdminResourceModel} from '../common/controller/signal-admin-resource-api';
import {SignalAdminListColumn, SignalAdminResourceListState, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {EarningsAccountApiService, EarningsAccountModel} from '../earnings-account/earnings-account-api.service';
import {SiteApiService, SiteModel} from '../site/site-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {SignalAdminResourceListComponent} from '../common/controller/signal-admin-resource-list.component';

type UsageParam = 'blocklist' | 'siteRuleset';

@Component({
  standalone: true,
  imports: [AppSharedModule, UIRouterModule, SignalAdminResourceListComponent],
  selector: 'adn-blocklist-edit-usage',
  template
})
export class BlocklistEditUsageComponent implements OnChanges {
  @Input() isNew = false;
  @Input() resourceId = '';
  @Input() usageParam: UsageParam = 'blocklist';

  activeTab: 'publishers' | 'sites' | 'adUnits' = 'publishers';

  readonly publisherColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'}
  ];
  readonly siteColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'siteUrl'},
    {param: 'SiteGroup', label: 'siteGroup'},
    {param: 'EarningsAccount', label: 'earningsAccount'}
  ];
  readonly adUnitColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: 'description'},
    {param: 'Site', label: 'site'}
  ];

  private readonly listFactory = inject(SignalAdminResourceListStateFactory);
  private readonly destroyRef = inject(DestroyRef);

  readonly publishers = this.createUsageList<EarningsAccountModel>(
    inject(EarningsAccountApiService),
    'fullPublishersForResourceUsage',
    'earningsAccount'
  );
  readonly sites = this.createUsageList<SiteModel>(
    inject(SiteApiService),
    'fullSitesForResourceUsage',
    'site'
  );
  readonly adUnits = this.createUsageList<AdUnitModel>(
    inject(AdUnitApiService),
    'fullAdUnitsForResourceUsage',
    'adUnit'
  );
  readonly perms: any;

  private loadedKey = '';

  constructor(@Inject(LocalUserPermissions) LocalUserPermissions: any) {
    this.perms = LocalUserPermissions.getAllVisPermissions();
  }

  ngOnChanges(): void {
    if (this.isNew || !this.resourceId) {
      this.loadedKey = '';
      this.clearLists();
      return;
    }
    const nextKey = `${this.usageParam}:${this.resourceId}`;
    if (nextKey === this.loadedKey) {
      return;
    }
    this.loadedKey = nextKey;
    const params = {[this.usageParam]: this.resourceId};
    this.publishers.setAdditionalParams(params);
    this.sites.setAdditionalParams(params);
    this.adUnits.setAdditionalParams(params);
  }

  selectTab(tab: 'publishers' | 'sites' | 'adUnits'): void {
    this.activeTab = tab;
  }

  private createUsageList<T extends SignalAdminResourceModel>(
    api: SignalAdminResourceApi<T>,
    listId: string,
    baseTable: string
  ): SignalAdminResourceListState<T> {
    return this.listFactory.create<T>(api, {
      listId,
      additionalParams: {},
      baseTable,
      syncPageToUrl: false
    }, this.destroyRef);
  }

  private clearLists(): void {
    [this.publishers, this.sites, this.adUnits].forEach((list) => {
      list.items.set([]);
      list.totalCount.set(0);
      list.selected.set({});
      list.loading.set(false);
    });
  }
}
